import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useRecognition } from "context/RecognitionContext";
import { useExport } from "context/ExportContext";
import { useLanguage } from "context/LanguageContext";

/**
 * Floating bottom-right panel showing:
 *  - Recognition batch progress (RecognitionContext)
 *  - XML export progress + Download button (ExportContext)
 * Rendered at app level — persists across navigation.
 */
export default function RecognitionProgressPanel() {
  const { batchState, cancelBatch, dismissDone: dismissRecognition } = useRecognition();
  const { exportState, dismissDone: dismissExport } = useExport();
  const { t } = useLanguage();

  /* ── Recognition ── */
  const {
    isRunning: recRunning,
    isDone: recDone,
    total,
    current,
    successCount,
    failCount,
    cancelled,
  } = batchState;
  const recVisible = recRunning || recDone;
  const recProgressValue = total > 0 ? Math.round((current / total) * 100) : 0;

  /* ── XML Export ── */
  const {
    isRunning: expRunning,
    isDone: expDone,
    isError: expError,
    progress: expProgress,
    message: expMessage,
    errorMessage: expErrorMessage,
    xmlContent,
    filename,
    exportLabel,
    warningMessage,
    missingSupplierInfos,
  } = exportState;
  const expVisible = expRunning || expDone;

  /* ── Auto-dismiss recognition done card after 8s ── */
  const recDismissRef = useRef(null);
  useEffect(() => {
    if (recDone && !recRunning) {
      recDismissRef.current = setTimeout(() => dismissRecognition(), 8000);
    }
    return () => clearTimeout(recDismissRef.current);
  }, [recDone, recRunning, dismissRecognition]);

  /* ── Auto-dismiss export error card after 10s ── */
  const expDismissRef = useRef(null);
  useEffect(() => {
    if (expDone && expError) {
      expDismissRef.current = setTimeout(() => dismissExport(), 10000);
    }
    return () => clearTimeout(expDismissRef.current);
  }, [expDone, expError, dismissExport]);

  /* ── Download XML ── */
  const handleDownload = () => {
    if (!xmlContent || !filename) return;
    const blob = new Blob([xmlContent], { type: "application/xml" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    dismissExport();
  };

  const panelVisible = recVisible || expVisible;

  return (
    <Collapse in={panelVisible} timeout={300} unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 2000,
          minWidth: 320,
          maxWidth: 420,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* ════ Recognition section ════ */}
        {recVisible && (
          <Box
            sx={{
              p: 2,
              borderLeft: "4px solid",
              borderColor: cancelled
                ? "warning.main"
                : recDone && failCount === 0
                ? "success.main"
                : recDone && failCount > 0
                ? "warning.main"
                : "info.main",
            }}
          >
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                {recRunning && !cancelled && <HourglassTopIcon fontSize="small" color="info" />}
                {recDone && failCount === 0 && !cancelled && (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                )}
                {recDone && (failCount > 0 || cancelled) && (
                  <CancelIcon fontSize="small" color="warning" />
                )}
                <Typography variant="subtitle2" fontWeight="bold">
                  {recRunning
                    ? t("recognitionInProgress") || "Recognition in progress"
                    : cancelled
                    ? t("recognitionCancelled") || "Recognition cancelled"
                    : t("recognitionFinished") || "Recognition finished"}
                </Typography>
              </Box>
              {recDone && (
                <IconButton size="small" onClick={dismissRecognition} aria-label="close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Counts */}
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t("documentsProcessed") || "Documents processed"}:{" "}
              <strong>
                {current}/{total}
              </strong>
              {(successCount > 0 || failCount > 0) && (
                <>
                  {" "}
                  &bull; <span style={{ color: "#4caf50" }}>✓ {successCount}</span>
                  {failCount > 0 && (
                    <>
                      {" "}
                      <span style={{ color: "#f44336" }}>✗ {failCount}</span>
                    </>
                  )}
                </>
              )}
            </Typography>

            {/* Progress bar */}
            {recRunning && (
              <LinearProgress
                variant={total > 0 ? "determinate" : "indeterminate"}
                value={recProgressValue}
                sx={{ borderRadius: 1, mb: 1.5 }}
              />
            )}

            {/* Done summary */}
            {recDone && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={cancelled || failCount > 0 ? 1 : 0}
              >
                {cancelled
                  ? t("recognitionWasCancelled") || "Batch was cancelled."
                  : failCount > 0
                  ? (t("recognitionSummary") || "Done. Success: {ok}, Failed: {fail}.")
                      .replace("{ok}", successCount)
                      .replace("{fail}", failCount)
                  : (
                      t("recognitionAllSuccess") || "All {ok} documents recognized successfully!"
                    ).replace("{ok}", String(successCount))}
              </Typography>
            )}

            {/* Cancel button */}
            {recRunning && !cancelled && (
              <Box display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={cancelBatch}
                  startIcon={<CancelIcon />}
                >
                  {t("cancelRecognition") || "Cancel recognition"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Separator between sections */}
        {recVisible && expVisible && <Divider />}

        {/* ════ XML Export section ════ */}
        {expVisible && (
          <Box
            sx={{
              p: 2,
              borderLeft: "4px solid",
              borderColor: expError ? "error.main" : expDone ? "success.main" : "secondary.main",
            }}
          >
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                {expRunning && <HourglassTopIcon fontSize="small" color="secondary" />}
                {expDone && !expError && (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                )}
                {expDone && expError && <ErrorOutlineIcon fontSize="small" color="error" />}
                <Typography variant="subtitle2" fontWeight="bold">
                  {expRunning
                    ? t("xmlExportInProgress") || "XML export in progress"
                    : expError
                    ? t("xmlExportFailed") || "XML export failed"
                    : t("xmlExportReady") || "XML ready to download"}
                </Typography>
              </Box>
              {expDone && (
                <IconButton size="small" onClick={dismissExport} aria-label="close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Label e.g. "Purchases · 42 docs" */}
            {exportLabel && (
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {exportLabel}
              </Typography>
            )}

            {/* Progress bar + status message */}
            {expRunning && (
              <>
                <LinearProgress
                  variant={expProgress > 0 ? "determinate" : "indeterminate"}
                  value={expProgress}
                  color="secondary"
                  sx={{ borderRadius: 1, mb: 1 }}
                />
                {expMessage && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {expMessage}
                  </Typography>
                )}
              </>
            )}

            {/* Error message */}
            {expDone && expError && (
              <Typography variant="caption" color="error" display="block" mb={1}>
                {expErrorMessage}
              </Typography>
            )}

            {/* Download button — shown once export is ready */}
            {expDone && !expError && xmlContent && (
              <>
                {!!warningMessage && (
                  <Box
                    sx={{
                      mt: 1,
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: "warning.light",
                    }}
                  >
                    <Typography variant="caption" color="warning.dark" display="block">
                      {warningMessage}
                    </Typography>
                    {Array.isArray(missingSupplierInfos) && missingSupplierInfos.length > 0 && (
                      <Typography variant="caption" color="warning.dark" display="block" mt={0.5}>
                        {missingSupplierInfos
                          .slice(0, 3)
                          .map(
                            (item) => item.supplier_name || item.supplier_registration || "Unknown"
                          )
                          .join(", ")}
                        {missingSupplierInfos.length > 3
                          ? ` … (+${missingSupplierInfos.length - 3})`
                          : ""}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                  gap={1}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {filename}
                  </Typography>
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    onClick={handleDownload}
                    startIcon={<DownloadIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    {t("downloadXml") || "Download XML"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Collapse>
  );
}
